// UnscopedScreenBadge.jsx
// The one diagnostic an OBS operator gets when a browser source doesn't know
// which booth it belongs to.
//
// With two events live, an overlay whose URL carries no ?s=CODE cannot
// resolve a shift (utils/shiftScope refuses to guess), so every scene renders
// its empty state — a blank-looking screen on a public display with no
// explanation. That is a misconfiguration the operator can fix in fifteen
// seconds IF they're told; silently blank, they'll be debugging a "broken"
// overlay mid-event.
//
// So: small, corner-pinned, and only ever visible in exactly that case —
// 2+ shifts live AND unresolved. One queue open, or a scoped URL, and this
// never renders. It stays deliberately understated rather than a full-screen
// error, because the screen it appears on is facing guests.
import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { auth, db } from "./../../firebase";
import { guestSafeLabel, resolveShift } from "./../../utils/shiftScope";
import { DEFAULT_BOOTH_CODE } from "./../../constants/booths";

export default function UnscopedScreenBadge() {
  const [authed, setAuthed] = useState(!!auth.currentUser);
  const [state, setState] = useState(null); // { candidates, reason, requested }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u || auth.currentUser) { setAuthed(true); return; }
      signInAnonymously(auth).catch(() => {});
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!authed) return;
    const q = query(collection(db, "shifts"), where("isActive", "==", true));
    return onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Must match useCurrentShiftOrders exactly, or the badge would
        // contradict the scene it's sitting on top of.
        const r = resolveShift(all, { defaultCode: DEFAULT_BOOTH_CODE });
        setState(r.needsChoice ? { candidates: all, reason: r.reason, requested: r.requested } : null);
      },
      () => setState(null)
    );
  }, [authed]);

  if (!state) return null;
  const { candidates, reason, requested } = state;

  // Two different operator mistakes, two different sentences. "Set to a booth
  // that isn't live" is the common one on an event morning — the rig was
  // configured the night before and that shift simply hasn't opened yet.
  const named = reason === "param-miss";

  return (
    <div
      style={{
        position: "fixed", left: 12, bottom: 12, zIndex: 99999,
        maxWidth: 420, padding: "9px 13px", borderRadius: 10,
        background: "rgba(13,24,51,0.93)",
        border: "1px solid rgba(252,179,31,0.6)",
        color: "#fcb31f",
        font: "600 13px/1.4 system-ui, sans-serif",
        pointerEvents: "none",
      }}
    >
      {/* Two different operator mistakes need two different sentences. The
          named case is the common one on an event morning: the rig was set up
          the night before and its shift simply hasn't been opened yet. */}
      <div>
        {named
          ? <>⚠️ This screen is set to booth <code style={{ color: "#fcb31f" }}>{requested}</code>, which isn’t open</>
          : <>⚠️ This screen isn’t assigned to a booth</>}
      </div>
      <div style={{ color: "rgba(255,255,255,0.72)", fontWeight: 400, marginTop: 3 }}>
        {named
          ? <>Open that queue with Booth code {requested}, or point this source at a live one.{" "}</>
          : <>{candidates.length} {candidates.length === 1 ? "queue is" : "queues are"} live. Add <code style={{ color: "#fcb31f" }}>?s=CODE</code> to this browser source’s URL.{" "}</>}
        {/* guestSafeLabel, not shiftLabel: this badge can be facing guests, so
            a private event is identified by its code rather than named. */}
        Live now: {candidates.length
          ? candidates.map(s => `${s.joinCode || "?"} (${guestSafeLabel(s)})`).join(" · ")
          : "nothing"}
      </div>
    </div>
  );
}
